export const PAYMENT_STATUS_OPTIONS = ['PENDING', 'CAPTURED', 'REFUNDED', 'FAILED'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUS_OPTIONS)[number];

export type PaymentSubject = {
  kind: 'BOOKING' | 'WALK_PLAN' | 'TRAINING_PACKAGE' | 'ADOPTION_REQUEST' | 'COMMERCE_ORDER';
  id: string;
  label: string;
  status: string;
  /** Null when the console has no page for that entity yet (plans, packages). */
  link_path: string | null;
  vendor: { id: string; business_name: string | null } | null;
  customer: { id: string; name: string | null; phone: string | null } | null;
};

export type AdminPayment = {
  id: string;
  amount_inr: string;
  refunded_amount_inr: string;
  refundable_inr: string;
  status: PaymentStatus;
  provider: string;
  is_dummy: boolean;
  order_ref: string | null;
  provider_ref: string | null;
  failure_reason: string | null;
  captured_at: string | null;
  refunded_at: string | null;
  created_at: string;
  /** Null for an orphaned payment — worth investigating rather than hiding. */
  subject: PaymentSubject | null;
};

export type PaymentRefundRow = {
  id: string;
  amount_inr: string;
  reason: string | null;
  actor_type: string;
  admin_user_id: string | null;
  created_at: string;
  /**
   * When somebody confirmed the money actually reached the customer.
   *
   * Null means outstanding. The platform does not run a gateway refund flow — a refund is
   * recorded when an admin decides it is owed, and the money moves separately — so a
   * refund row on its own says "we owe this", never "we paid this".
   */
  settled_at: string | null;
  settlement_ref: string | null;
  settled_by_user_id: string | null;
  settlement_note: string | null;
};

/** A refund the customer has not been confirmed to have received. */
export function is_outstanding(r: PaymentRefundRow): boolean {
  return r.settled_at === null;
}

export type AdminPaymentDetail = AdminPayment & { refunds: PaymentRefundRow[] };

export type PaymentListResponse = {
  items: AdminPayment[];
  total: number;
  page: number;
  page_size: number;
  /** Totals for the whole filter, not the page — a per-page sum means nothing. */
  totals: { gross_inr: string; refunded_inr: string; net_inr: string };
};

export const PAYMENT_SORT_FIELDS = ['created_at', 'amount_inr', 'captured_at'] as const;

export type PaymentFilters = {
  page?: number;
  page_size?: number;
  status?: PaymentStatus | '';
  q?: string;
  is_dummy?: string;
  /** Any money given back, partial or full. */
  refunded?: string;
  /** Has at least one refund nobody has confirmed paying out. */
  unsettled?: string;
  /** Refunded in part but not in full — the state `status` has no word for. */
  partially_refunded?: string;
  /** A payment with no booking, order or plan behind it. */
  orphaned?: string;
  /** Inclusive created-date range, YYYY-MM-DD. */
  from?: string;
  to?: string;
};

/** What the tiles above the ledger count — the whole book, not the current filter. */
export type PaymentCounts = {
  captured: number;
  pending: number;
  failed: number;
  refunded_any: number;
  /**
   * Refunded in part, still reading as CAPTURED.
   *
   * A partial refund does not move the status, so the enum cannot express "half of this
   * has already gone back" — and a revenue figure that trusts the status is wrong by
   * exactly this much.
   */
  partially_refunded: number;
  /** Money the platform took and cannot attribute to anything. */
  orphaned: number;
  gross_inr: string;
  refunded_inr: string;
  net_inr: string;
  /** Money a customer started paying and never completed. */
  pending_inr: string;
  /**
   * Refunds decided but not yet confirmed as paid out.
   *
   * The number that answers "who is still owed money" — impossible to get before, because
   * nothing separated a refund we decided from a refund we paid.
   */
  unsettled_refunds: number;
  unsettled_inr: string;
};

export const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
  PENDING: 'Never completed',
  CAPTURED: 'Taken',
  REFUNDED: 'Given back',
  FAILED: 'Failed',
};

export const PAYMENT_STATUS_TONE: Record<PaymentStatus, 'success' | 'warning' | 'neutral' | 'destructive'> = {
  PENDING: 'warning',
  CAPTURED: 'success',
  REFUNDED: 'neutral',
  FAILED: 'destructive',
};

/** What the money on this payment actually did, in one phrase. */
export function money_story(p: AdminPayment): string {
  const refunded = Number(p.refunded_amount_inr);
  if (p.status === 'PENDING') return 'Started, never completed';
  if (p.status === 'FAILED') return p.failure_reason ?? 'The gateway refused it';
  if (p.status === 'REFUNDED') return 'All of it went back';
  if (refunded > 0) return `Part of it went back — ₹${p.refundable_inr} kept`;
  return 'Kept in full';
}

/** True when the status says CAPTURED but some of the money has already gone back. */
export function is_partly_refunded(p: AdminPayment): boolean {
  return p.status === 'CAPTURED' && Number(p.refunded_amount_inr) > 0;
}

export const SUBJECT_LABEL: Record<PaymentSubject['kind'], string> = {
  BOOKING: 'Booking',
  WALK_PLAN: 'Walk plan',
  TRAINING_PACKAGE: 'Training package',
  ADOPTION_REQUEST: 'Adoption unlock',
  COMMERCE_ORDER: 'Shop order',
};
